import { useState, useMemo } from 'react';
import { Activity } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Table, type Column } from '@/shared/components/ui/Table';
import { LineChart } from '@/shared/components/Charts/LineChart';
import { useActivityAnalytics } from '../hooks/useAnalytics';
import { useEmployeeLabels } from '../hooks/useEmployeeLabels';
import { PanelState } from './PanelState';
import { formatCount, defaultDateRange } from '../constants';
import type { EmployeeActivity } from '../types';
import { useUser } from '@/shared/hooks/useAuth';

export function ActivityPanel({ enabled }: { enabled: boolean }) {
  const [range, setRange] = useState(defaultDateRange);
  const user = useUser();

  const { data, isLoading, isError } = useActivityAnalytics({
    from: range.from,
    to: range.to,
    enabled,
  });
  const { resolve } = useEmployeeLabels(enabled);

  const columns = useMemo<Column<EmployeeActivity>[]>(() => {
    const base: Column<EmployeeActivity>[] = [
      {
        key: 'employee',
        header: 'Employee',
        render: (r) => {
          const { name, tier } = resolve(r.userId);
          return (
            <div className="flex flex-col">
              <span className="font-medium text-on-surface">{name}</span>
              {tier && <span className="text-label-caps uppercase text-on-surface-variant">{tier}</span>}
            </div>
          );
        },
      },
    ];

    if (user?.role === 'superadmin' || user?.department === 'sales') {
      return [
        ...base,
        { key: 'leadsAdded', header: 'Leads added', align: 'right', render: (r) => formatCount(r.leadsAdded) },
        { key: 'callsLogged', header: 'Calls logged', align: 'right', render: (r) => formatCount(r.callsLogged) },
        { key: 'dealChanges', header: 'Deal changes', align: 'right', render: (r) => formatCount(r.dealChanges) },
      ];
    }

    return [
      ...base,
      { key: 'tasksChanged', header: 'Task changes', align: 'right', render: (r) => formatCount(r.tasksChanged) },
      { key: 'projectsChanged', header: 'Project changes', align: 'right', render: (r) => formatCount(r.projectsChanged) },
    ];
  }, [resolve, user?.role, user?.department]);

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Activity className="h-4 w-4" /> Team Activity
          </span>
        }
        subtitle="Daily activity volume and per-employee contributions."
        action={
          <div className="flex items-center gap-2">
            <div className="w-36">
              <Input
                name="activity-from"
                type="date"
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <span className="text-on-surface-variant">–</span>
            <div className="w-36">
              <Input
                name="activity-to"
                type="date"
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
          </div>
        }
      />
      <CardBody className="space-y-4">
        <PanelState
          isLoading={isLoading}
          isError={isError}
          isEmpty={!!data?.empty}
          emptyTitle="No activity in this range"
          emptyDescription="No activity was recorded in the selected window for the current scope."
          emptyIcon={<Activity className="h-6 w-6" />}
        >
          <LineChart
            data={data?.dailyActivity ?? []}
            xKey="date"
            yKey="count"
            color="#6E62FF"
            height={240}
          />
          <Table
            columns={columns}
            data={data?.byEmployee ?? []}
            rowKey={(r) => r.userId}
            empty="No per-employee activity."
          />
        </PanelState>
      </CardBody>
    </Card>
  );
}
